import { type Locale, enUS, sk } from 'date-fns/locale';

// Map a next-intl locale ('sk' | 'en') to its date-fns `Locale`, so relative-time
// helpers (formatDistanceToNow, etc.) render in the active UI language instead of
// defaulting to English. Anything that isn't 'sk' falls back to en-US.
const dateFnsLocale = (locale: string): Locale => (locale === 'sk' ? sk : enUS);

// Helper function to format Date to YYYY-MM-DD string
const formatDateToYYYYMMDD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Takes "YYYY-MM-DDTHH:mm:ss" or "YYYY-MM-DD" → "YYYY-MM-DD"
const normalizeToDateInput = (raw: string): string => raw.split('T')[0] ?? raw;

// Human-readable date: "Apr 20, 2026" (en) / "20. 4. 2026" (sk). Returns "—" for empty.
const formatDateHuman = (
  iso: string | null | undefined,
  locale: string
): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(
      locale === 'sk' ? 'sk-SK' : 'en-US',
      { year: 'numeric', month: 'short', day: 'numeric' }
    );
  } catch {
    return iso;
  }
};

// Daily usage quotas reset at UTC midnight (backend: time_until_next_utc_midnight()),
// not the user's local midnight — for Slovak users (UTC+1/UTC+2) that's 01:00/02:00
// local time. Computes the next UTC-midnight instant and formats it as a local HH:mm
// string so "resets at midnight" copy can show the actual local time instead.
// DST is handled automatically by Intl's Europe/Bratislava timezone conversion.
const formatNextUtcMidnightLocalTime = (locale: string): string => {
  const now = new Date();
  const nextUtcMidnight = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
  );
  return new Intl.DateTimeFormat(locale === 'sk' ? 'sk-SK' : 'en-GB', {
    timeZone: 'Europe/Bratislava',
    hour: '2-digit',
    minute: '2-digit',
  }).format(nextUtcMidnight);
};

// Human-readable date for a BARE "YYYY-MM-DD" value (no time-of-day
// component) — e.g. `brief.den`, `line.posledny_den`, `cakame_na[].dokedy`
// (#9683). `new Date('2026-09-05')` parses a date-only ISO string as UTC
// midnight (per the spec), so formatting it with the browser's local
// timezone via `toLocaleDateString` re-interprets it: a viewer at a negative
// UTC offset (e.g. US Pacific) sees the PREVIOUS calendar day — silently
// wrong for a preclusive deadline. Force `timeZone: 'UTC'` so the same
// three digits always render the same calendar day, everywhere. Do NOT use
// this for an actual instant (e.g. `generating_event.occurred_at`) — that
// value legitimately varies by the viewer's local time and should render via
// `toLocaleString` instead.
const formatBareDate = (iso: string, locale: string): string => {
  // An unparseable string yields an Invalid Date, whose toLocale*String
  // RETURNS the literal "Invalid Date" rather than throwing — so the guard is
  // on the parsed value, not a catch. The raw input is the honest fallback.
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(locale === 'sk' ? 'sk-SK' : 'en-US', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Human-readable INSTANT (date + local time) for a real timestamp — e.g.
// `generating_event.occurred_at` (#9683). Unlike `formatBareDate`, this
// legitimately varies by the viewer's local timezone: an instant is a single
// point in universal time, and showing it in local time is exactly what a
// reader expects ("this fired at 14:32 my time"), the opposite of the
// bare-date case above.
const formatInstant = (iso: string, locale: string): string => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(locale === 'sk' ? 'sk-SK' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

export {
  formatDateToYYYYMMDD,
  normalizeToDateInput,
  formatDateHuman,
  formatBareDate,
  formatInstant,
  dateFnsLocale,
  formatNextUtcMidnightLocalTime,
};
